import React, { useEffect, useRef } from 'react';
import type { LucideIcon } from 'lucide-react';

export interface Stage {
  id: number;
  title: string;
  description: string;
  icon: LucideIcon;
  image: string;
  action: string;
}

interface StageIndicatorProps {
  stages: Stage[];
  currentStage: number;
  isHistoryMode: boolean;
  stageImages: { [key: number]: string | null | undefined };
  onStageSelect: (id: number) => void;
}

export const StageIndicator: React.FC<StageIndicatorProps> = ({
  stages,
  currentStage,
  isHistoryMode,
  stageImages,
  onStageSelect
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    if (scrollContainerRef.current && itemRefs.current[currentStage - 1]) {
      const container = scrollContainerRef.current;
      const activeItem = itemRefs.current[currentStage - 1];

      if (activeItem) {
        const containerWidth = container.offsetWidth;
        const itemLeft = activeItem.offsetLeft;
        const itemWidth = activeItem.offsetWidth;
        
        // Calculate the scroll position to center the active item
        const scrollLeft = itemLeft - (containerWidth / 2) + (itemWidth / 2);

        container.scrollTo({
          left: scrollLeft,
          behavior: 'smooth'
        });
      }
    }
  }, [currentStage]);

  return (
    <div 
      ref={scrollContainerRef}
      className="relative flex overflow-x-auto gap-3 md:gap-4 mb-8 md:mb-12 pb-2 -mx-4 px-4 md:mx-0 md:px-0 scrollbar-hide snap-x"
      style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
    >
      {stages.map((stage, index) => {
        // Logic: Show image if history item OR stage is passed/current and has an image
        const showImage = isHistoryMode || stage.id < currentStage;
        const stageImage = stageImages[stage.id];
        
        // Check if this stage has a custom image to show
        const hasStageImage = !!stageImage;
        const shouldShowImage = showImage || (hasStageImage && stage.id === currentStage); // Show current stage image if available
        
        const displayImage = (shouldShowImage && stageImage) ? stageImage : stage.image;
        
        // Check if we should actually show the image (if it's the default placeholder, maybe we still show it if "shouldShowImage" is false? 
        // In original code: `!shouldShowImage ? 'bg-gray-50' : ''`. 
        // And `shouldShowImage` was `showImage || hasStageImage`.
        
        const effectiveShouldShowImage = showImage || hasStageImage;

        return (
          <div 
            key={stage.id}
            ref={el => { itemRefs.current[index] = el }}
            onClick={() => {
              if (isHistoryMode || stage.id <= currentStage) {
                onStageSelect(stage.id);
              }
            }}
            className={`relative overflow-hidden rounded-lg border transition-all duration-300 cursor-pointer w-[160px] md:w-[200px] flex-shrink-0 snap-center ${
              currentStage === stage.id 
                ? 'border-black ring-2 ring-black/5 shadow-lg opacity-100' 
                : (isHistoryMode || stage.id < currentStage) ? 'border-gray-200 opacity-100' : 'border-gray-100 opacity-40'
            }`}
          >
             <div className={`aspect-[4/3] bg-gray-100 relative ${!effectiveShouldShowImage ? 'bg-gray-50' : ''}`}>
                {effectiveShouldShowImage ? (
                    <>
                        <img src={displayImage} alt={stage.title} className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-black/30" />
                    </>
                ) : (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-100 text-gray-300">
                        <stage.icon className="w-8 h-8 opacity-20" />
                    </div>
                )}
                
                <div className={`absolute bottom-0 left-0 right-0 p-3 ${effectiveShouldShowImage ? 'text-white' : 'text-gray-400'}`}>
                    <div className="flex items-center gap-2">
                        <div className={`p-1.5 rounded-full ${effectiveShouldShowImage ? 'bg-white/20 backdrop-blur-sm' : 'bg-gray-200'}`}>
                            <stage.icon className="w-3 h-3" />
                        </div>
                        <span className="font-bold text-sm whitespace-nowrap overflow-hidden text-ellipsis">{stage.title}</span>
                    </div>
                </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

